import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { openHireReconciliationPrint } from '@/components/pmv/PrintablePmvCommercial';
import { useHireMasterLabels } from '@/hooks/useHireMasterLabels';
import pmvHireService, {
  formatHireMoney,
  type PmvHireAgreement,
  type PmvHireReconciliation,
  type PmvHireStatement,
} from '@/services/pmv-hire.service';
import { purchaseOrderService } from '@/services/purchaseOrder.service';
import { supplierInvoiceService } from '@/services/supplierInvoice.service';
import {
  HIRE_DOC_STATUS_COLOR,
  formatHireAgreementOption,
  formatHireStatementOption,
  hireTableCellEllipsis,
  hireTableLinkBtnStyle,
  resolveLabel,
} from '@/utils/pmvHireUi';

const { Text, Title } = Typography;

const panelStyle: React.CSSProperties = {
  border: '1px solid #d9d9d9',
  padding: 16,
  minHeight: 200,
  background: '#fafafa',
  borderRadius: 6,
};

/** Three-column: PMV EXPECTED | VENDOR INVOICE | VARIANCE */
const PmvHireInvoiceReconPage: React.FC = () => {
  const { id } = useParams();
  const [params] = useSearchParams();
  const isDetail = !!id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [status, setStatus] = useState<string | undefined>();
  const [createOpen, setCreateOpen] = useState(!!params.get('statement'));
  const [resolveOpen, setResolveOpen] = useState(false);
  const [form] = Form.useForm();
  const [resolveForm] = Form.useForm();
  const watchedAgreementId = Form.useWatch('hire_agreement_id', form);
  const watchedInvoiceId = Form.useWatch('supplier_invoice_id', form);

  const { supplierLabelById, equipmentLabelById, poLabelById } = useHireMasterLabels({
    enablePos: true,
  });

  const agreementsQ = useQuery({
    queryKey: ['pmv-hire-agreements-for-recon'],
    queryFn: () => pmvHireService.listAgreements({ limit: 200 }),
    staleTime: 30_000,
  });

  const agreementById = useMemo(() => {
    const m = new Map<string, PmvHireAgreement>();
    for (const a of agreementsQ.data?.items ?? []) m.set(a.id, a);
    return m;
  }, [agreementsQ.data]);

  const selectedAgreement = watchedAgreementId
    ? agreementById.get(watchedAgreementId)
    : undefined;

  const statementsQ = useQuery({
    queryKey: ['pmv-hire-stmt-for-recon', watchedAgreementId],
    queryFn: () =>
      pmvHireService.listStatements({
        hire_agreement_id: watchedAgreementId,
        status: 'CERTIFIED',
        limit: 100,
      }),
    enabled: createOpen && !!watchedAgreementId,
  });

  const invoicesQ = useQuery({
    queryKey: ['pmv-hire-si-for-recon', selectedAgreement?.supplier_id],
    queryFn: () =>
      supplierInvoiceService.list({
        supplier_id: selectedAgreement!.supplier_id,
      }),
    enabled: createOpen && !!selectedAgreement?.supplier_id,
  });

  const posQ = useQuery({
    queryKey: ['pmv-hire-pos-for-recon', selectedAgreement?.supplier_id],
    queryFn: () =>
      purchaseOrderService.list({
        supplier_id: selectedAgreement!.supplier_id,
      }),
    enabled: createOpen && !!selectedAgreement?.supplier_id,
  });

  useEffect(() => {
    if (!createOpen) return;
    const statementId = params.get('statement');
    const agreementId = params.get('agreement');
    if (agreementId) form.setFieldsValue({ hire_agreement_id: agreementId });
    if (statementId) form.setFieldsValue({ statement_id: statementId });
  }, [createOpen, form, params]);

  // Prefill invoiced amount from selected supplier invoice
  useEffect(() => {
    if (!watchedInvoiceId || !invoicesQ.data) return;
    const inv = invoicesQ.data.find((i) => i.id === watchedInvoiceId);
    if (!inv) return;
    if (inv.grand_total != null) {
      form.setFieldsValue({ invoiced_amount: Number(inv.grand_total) });
    }
    if (inv.po_id && !form.getFieldValue('purchase_order_id')) {
      form.setFieldsValue({ purchase_order_id: inv.po_id });
    }
  }, [watchedInvoiceId, invoicesQ.data, form]);

  const listQ = useQuery({
    queryKey: ['pmv-hire-recons', status],
    queryFn: () => pmvHireService.listReconciliations({ status, limit: 100 }),
    enabled: !isDetail,
  });

  const detailQ = useQuery({
    queryKey: ['pmv-hire-recon', id],
    queryFn: () => pmvHireService.getReconciliation(id!),
    enabled: isDetail,
  });

  const createMut = useMutation({
    mutationFn: (body: Record<string, unknown>) => pmvHireService.createReconciliation(body),
    onSuccess: (r) => {
      message.success('Reconciliation created');
      setCreateOpen(false);
      form.resetFields();
      navigate(`/asset/pmv/hire/reconciliations/${r.id}`);
    },
    onError: (e: Error) => message.error(e.message || 'Create failed'),
  });

  const resolveMut = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      pmvHireService.resolveReconciliation(id!, body),
    onSuccess: () => {
      message.success('Resolved (AP handoff status only — no AP/GL post)');
      setResolveOpen(false);
      resolveForm.resetFields();
      qc.invalidateQueries({ queryKey: ['pmv-hire-recon', id] });
      qc.invalidateQueries({ queryKey: ['pmv-hire-recons'] });
    },
    onError: (e: Error) => message.error(e.message || 'Resolve failed'),
  });

  if (isDetail) {
    const r = detailQ.data;
    const agreement = r?.hire_agreement_id ? agreementById.get(r.hire_agreement_id) : undefined;
    const currency = agreement?.currency;

    return (
      <WorkspaceLayout>
        <EnterprisePageHeader
          documentLabel="Invoice Reconciliation"
          documentNumber={r?.recon_number || r?.id?.slice(0, 8)}
          subtitle="Expected hire statement vs vendor invoice — AP remains payable authority"
          statusTag={
            r ? (
              <Tag color={HIRE_DOC_STATUS_COLOR[r.status] || 'default'}>{r.status}</Tag>
            ) : undefined
          }
          primaryActions={
            <Space wrap>
              <Button onClick={() => navigate('/asset/pmv/hire/reconciliations')}>List</Button>
              <Button
                icon={<PrinterOutlined />}
                disabled={!r}
                onClick={() => {
                  if (!r) return;
                  const agreement = r.hire_agreement_id
                    ? agreementById.get(r.hire_agreement_id)
                    : undefined;
                  openHireReconciliationPrint({
                    recon: r,
                    agreementLabel: agreement
                      ? formatHireAgreementOption(agreement)
                      : r.hire_agreement_id,
                    supplierLabel: agreement
                      ? resolveLabel(supplierLabelById, agreement.supplier_id)
                      : undefined,
                    equipmentLabel: agreement
                      ? resolveLabel(equipmentLabelById, agreement.pmv_profile_id)
                      : undefined,
                    statementLabel: r.statement_id
                      ? `Statement ${r.statement_id.slice(0, 8)}…`
                      : undefined,
                    currency: agreement?.currency,
                  });
                }}
              >
                Print
              </Button>
              {r?.hire_agreement_id ? (
                <Button
                  onClick={() => navigate(`/asset/pmv/hire/agreements/${r.hire_agreement_id}`)}
                >
                  Agreement
                </Button>
              ) : null}
              {r?.statement_id ? (
                <Button onClick={() => navigate(`/asset/pmv/hire/statements/${r.statement_id}`)}>
                  Statement
                </Button>
              ) : null}
              <Button
                type="primary"
                disabled={['CLOSED', 'DISMISSED', 'MATCHED'].includes(String(r?.status))}
                onClick={() => {
                  resolveForm.setFieldsValue({
                    status: 'MATCHED',
                    resolution_notes: '',
                  });
                  setResolveOpen(true);
                }}
              >
                Resolve
              </Button>
            </Space>
          }
        />
        <Alert
          style={{ marginBottom: 12 }}
          type="info"
          showIcon
          message="Neutral variance language. PMV never posts Supplier Invoice / AP / GL."
        />

        <Card size="small" style={{ marginBottom: 12 }}>
          <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }} bordered>
            <Descriptions.Item label="Agreement">
              {r?.hire_agreement_id ? (
                <Link to={`/asset/pmv/hire/agreements/${r.hire_agreement_id}`}>
                  {agreement
                    ? formatHireAgreementOption(agreement)
                    : r.hire_agreement_id.slice(0, 8)}
                </Link>
              ) : (
                '—'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Equipment">
              {agreement ? resolveLabel(equipmentLabelById, agreement.pmv_profile_id) : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Supplier">
              {agreement ? resolveLabel(supplierLabelById, agreement.supplier_id) : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="PO">
              {r?.purchase_order_id
                ? resolveLabel(poLabelById, r.purchase_order_id, r.purchase_order_id.slice(0, 8))
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="AP handoff">
              <Tag>{r?.ap_handoff_status || 'NONE'}</Tag>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Row gutter={12}>
          <Col xs={24} md={8}>
            <div style={panelStyle}>
              <Title level={5}>PMV EXPECTED</Title>
              <Text type="secondary">From certified hire statement</Text>
              <div style={{ fontSize: 28, fontWeight: 600, marginTop: 12 }}>
                {formatHireMoney(r?.expected_amount, currency)}
              </div>
              <div style={{ marginTop: 8 }}>
                {r?.statement_id ? (
                  <Link to={`/asset/pmv/hire/statements/${r.statement_id}`}>Open statement</Link>
                ) : (
                  <Text type="secondary">No statement</Text>
                )}
              </div>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div style={panelStyle}>
              <Title level={5}>VENDOR INVOICE</Title>
              <Text type="secondary">Referenced Supplier Invoice amount</Text>
              <div style={{ fontSize: 28, fontWeight: 600, marginTop: 12 }}>
                {formatHireMoney(r?.invoiced_amount, currency)}
              </div>
              <div style={{ marginTop: 8 }}>
                {r?.supplier_invoice_id ? (
                  <Text type="secondary">
                    Invoice ref {r.supplier_invoice_id.slice(0, 8)}…
                  </Text>
                ) : (
                  <Text type="secondary">No invoice linked</Text>
                )}
              </div>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div style={{ ...panelStyle, background: '#fff7e6' }}>
              <Title level={5}>VARIANCE</Title>
              <Text type="secondary">{r?.variance_type || '—'}</Text>
              <div style={{ fontSize: 28, fontWeight: 600, marginTop: 12 }}>
                {formatHireMoney(r?.variance_amount, currency)}
              </div>
              <Text>{r?.variance_note || '—'}</Text>
            </div>
          </Col>
        </Row>

        <Tabs
          style={{ marginTop: 12 }}
          items={[
            {
              key: 'lines',
              label: `Line variance (${r?.lines?.length ?? 0})`,
              children: (
                <Card size="small">
                  <Table
                    rowKey={(row, i) => String((row as { id?: string }).id || i)}
                    size="small"
                    dataSource={r?.lines ?? []}
                    pagination={false}
                    locale={{ emptyText: <Empty description="No line-level variance" /> }}
                    columns={[
                      { title: 'Line', dataIndex: 'description', ellipsis: true },
                      {
                        title: 'Expected',
                        dataIndex: 'expected_amount',
                        render: (v) => formatHireMoney(v, currency),
                      },
                      {
                        title: 'Invoiced',
                        dataIndex: 'invoiced_amount',
                        render: (v) => formatHireMoney(v, currency),
                      },
                      {
                        title: 'Variance',
                        dataIndex: 'variance_amount',
                        render: (v) => formatHireMoney(v, currency),
                      },
                    ]}
                  />
                </Card>
              ),
            },
          ]}
        />

        <Modal
          title="Resolve reconciliation"
          open={resolveOpen}
          onCancel={() => setResolveOpen(false)}
          onOk={() => resolveForm.submit()}
          confirmLoading={resolveMut.isPending}
          destroyOnClose
        >
          <Alert
            style={{ marginBottom: 12 }}
            type="warning"
            showIcon
            message="Resolution updates PMV handoff status only. Supplier Invoice / AP / GL stay in Procurement & Finance."
          />
          <Form
            form={resolveForm}
            layout="vertical"
            onFinish={(v) => resolveMut.mutate(v)}
          >
            <Form.Item name="status" label="Resolution" rules={[{ required: true }]}>
              <Select
                options={[
                  { value: 'MATCHED', label: 'Matched' },
                  { value: 'DISPUTED', label: 'Disputed' },
                  { value: 'VARIANCE', label: 'Variance accepted (commercial note)' },
                  { value: 'CLOSED', label: 'Closed' },
                  { value: 'DISMISSED', label: 'Dismissed' },
                ]}
              />
            </Form.Item>
            <Form.Item name="resolution_notes" label="Notes" rules={[{ required: true }]}>
              <Input.TextArea rows={3} placeholder="Commercial resolution rationale" />
            </Form.Item>
          </Form>
        </Modal>
      </WorkspaceLayout>
    );
  }

  const listItems = listQ.data?.items ?? [];

  return (
    <WorkspaceLayout>
      <EnterprisePageHeader
        documentLabel="Invoice Reconciliation"
        subtitle="Expected hire statement vs vendor invoice — AP remains payable authority"
        primaryActions={
          <Space wrap>
            <Button
              type="primary"
              onClick={() => {
                form.resetFields();
                setCreateOpen(true);
              }}
            >
              New reconciliation
            </Button>
            <Button onClick={() => navigate('/asset/pmv/hire/control')}>Hire Control</Button>
            <Button onClick={() => navigate('/asset/pmv/hire/statements')}>Statements</Button>
          </Space>
        }
        extra={
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 160 }}
            value={status}
            onChange={setStatus}
            options={['OPEN', 'MATCHED', 'VARIANCE', 'DISPUTED', 'CLOSED', 'DISMISSED'].map(
              (v) => ({
                value: v,
                label: v,
              })
            )}
          />
        }
      />
      <Alert
        style={{ marginBottom: 12 }}
        type="info"
        showIcon
        message="Link certified statement + supplier invoice by master select. PMV does not create or post invoices."
      />
      {!listQ.isLoading && listItems.length === 0 ? (
        <Empty description="No reconciliations yet">
          <Button type="primary" onClick={() => setCreateOpen(true)}>
            New reconciliation
          </Button>
        </Empty>
      ) : (
        <Table
          rowKey="id"
          size="small"
          loading={listQ.isLoading}
          dataSource={listItems}
          pagination={{ pageSize: 25 }}
          scroll={{ x: 1280 }}
          tableLayout="fixed"
          columns={[
            {
              title: 'Recon',
              dataIndex: 'recon_number',
              width: 180,
              ellipsis: true,
              render: (v: string, r: PmvHireReconciliation) => (
                <div style={hireTableCellEllipsis}>
                  <Button
                    type="link"
                    style={hireTableLinkBtnStyle}
                    onClick={() => navigate(`/asset/pmv/hire/reconciliations/${r.id}`)}
                  >
                    {v || r.id.slice(0, 8)}
                  </Button>
                </div>
              ),
            },
            {
              title: 'Agreement',
              dataIndex: 'hire_agreement_id',
              width: 200,
              ellipsis: true,
              render: (aid: string) => {
                const a = agreementById.get(aid);
                return (
                  <div style={hireTableCellEllipsis}>
                    {a ? (
                      <Link to={`/asset/pmv/hire/agreements/${aid}`}>{a.agreement_number}</Link>
                    ) : (
                      aid?.slice(0, 8) || '—'
                    )}
                  </div>
                );
              },
            },
            {
              title: 'Supplier',
              width: 200,
              ellipsis: true,
              render: (_: unknown, r: PmvHireReconciliation) => {
                const a = agreementById.get(r.hire_agreement_id);
                return (
                  <div style={hireTableCellEllipsis}>
                    {a ? resolveLabel(supplierLabelById, a.supplier_id) : '—'}
                  </div>
                );
              },
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 120,
              render: (s: string) => (
                <Tag color={HIRE_DOC_STATUS_COLOR[s] || 'default'}>{s}</Tag>
              ),
            },
            {
              title: 'Variance type',
              dataIndex: 'variance_type',
              width: 140,
              ellipsis: true,
            },
            {
              title: 'Expected',
              dataIndex: 'expected_amount',
              width: 130,
              align: 'right' as const,
              render: (v, r) =>
                formatHireMoney(v, agreementById.get(r.hire_agreement_id)?.currency),
            },
            {
              title: 'Invoiced',
              dataIndex: 'invoiced_amount',
              width: 130,
              align: 'right' as const,
              render: (v, r) =>
                formatHireMoney(v, agreementById.get(r.hire_agreement_id)?.currency),
            },
            {
              title: 'Variance',
              dataIndex: 'variance_amount',
              width: 130,
              align: 'right' as const,
              render: (v, r) =>
                formatHireMoney(v, agreementById.get(r.hire_agreement_id)?.currency),
            },
          ]}
        />
      )}
      <Modal
        title="Create reconciliation"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={createMut.isPending}
        width={680}
        destroyOnClose
      >
        <Alert
          style={{ marginBottom: 12 }}
          type="info"
          showIcon
          message="Select agreement → certified statement → supplier invoice. Expected amount comes from the statement."
        />
        <Form
          form={form}
          layout="vertical"
          onFinish={(v) =>
            createMut.mutate({
              hire_agreement_id: v.hire_agreement_id,
              statement_id: v.statement_id,
              invoiced_amount: v.invoiced_amount,
              supplier_invoice_id: v.supplier_invoice_id || null,
              purchase_order_id: v.purchase_order_id || null,
            })
          }
        >
          <Form.Item
            name="hire_agreement_id"
            label="Hire agreement"
            rules={[{ required: true }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Search agreement"
              options={(agreementsQ.data?.items ?? []).map((a) => ({
                value: a.id,
                label: formatHireAgreementOption(a),
              }))}
              onChange={() =>
                form.setFieldsValue({
                  statement_id: undefined,
                  supplier_invoice_id: undefined,
                  purchase_order_id: undefined,
                  invoiced_amount: undefined,
                })
              }
            />
          </Form.Item>
          {selectedAgreement ? (
            <Descriptions size="small" column={1} style={{ marginBottom: 12 }}>
              <Descriptions.Item label="Supplier">
                {resolveLabel(supplierLabelById, selectedAgreement.supplier_id)}
              </Descriptions.Item>
              <Descriptions.Item label="Equipment">
                {resolveLabel(equipmentLabelById, selectedAgreement.pmv_profile_id)}
              </Descriptions.Item>
            </Descriptions>
          ) : null}
          <Form.Item name="statement_id" label="Certified statement" rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="label"
              disabled={!watchedAgreementId}
              loading={statementsQ.isLoading}
              placeholder={
                watchedAgreementId ? 'Select certified statement' : 'Select agreement first'
              }
              options={(statementsQ.data?.items ?? []).map((s: PmvHireStatement) => ({
                value: s.id,
                label: formatHireStatementOption(s),
              }))}
            />
          </Form.Item>
          <Form.Item name="supplier_invoice_id" label="Supplier invoice">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              disabled={!selectedAgreement?.supplier_id}
              loading={invoicesQ.isLoading}
              placeholder="Select vendor invoice (Procurement)"
              options={(invoicesQ.data ?? []).map((inv) => ({
                value: inv.id,
                label: `${inv.supplier_invoice_number || inv.invoice_number || inv.id.slice(0, 8)} · ${inv.invoice_status || ''} · ${formatHireMoney(
                  inv.grand_total,
                  inv.currency || selectedAgreement?.currency
                )}`,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="invoiced_amount"
            label={`Invoiced amount${selectedAgreement?.currency ? ` (${selectedAgreement.currency})` : ''}`}
            rules={[{ required: true, message: 'Enter invoiced amount' }]}
          >
            <InputNumber style={{ width: '100%' }} min={0} precision={2} />
          </Form.Item>
          <Form.Item name="purchase_order_id" label="Purchase order">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              disabled={!selectedAgreement?.supplier_id}
              loading={posQ.isLoading}
              placeholder="Optional PO link"
              options={(posQ.data ?? []).map((po) => ({
                value: po.id,
                label: `${po.po_number} · ${po.po_status || ''}`,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </WorkspaceLayout>
  );
};

export default PmvHireInvoiceReconPage;
